/**
 * Canonical clinician-pin ↔ biomarker matching for the PATIENT surfaces (web + mobile).
 *
 * Clinician pins are stored as source-qualified row keys, not bare LOINCs, because the
 * clinician table lets a provider pin one specific source row. A stored pin key can be:
 *   - `80404-7`                              (bare LOINC — legacy/single-source)
 *   - `80404-7__oura`                        (LOINC + source slug)
 *   - `loinc-unit::80404-7::unit=ms__oura`   (unit-split LOINC + source slug)
 *
 * The patient surfaces present a biomarker as a single concept (one row per LOINC on
 * mobile; a base-LOINC "care team pinned this" section on web), so they must match a pin
 * to a biomarker by BASE LOINC — a naive `pinnedRowKeys.includes(loincCode)` silently
 * misses every source-qualified or unit-split pin (a clinician pinning a wearable HRV row
 * would never surface for the patient). Reduce both the row's own code and the pin key to
 * their base LOINC before comparing.
 *
 * This is the single source of truth: patient web and mobile both import these helpers so
 * a pin registers identically on either surface. The clinician authoring table keeps its
 * own source-EXACT matching (see `apps/web/components/practice/ehr/trends/biomarker-order.ts`)
 * — that asymmetry is intentional: the clinician pins a specific source; the patient sees
 * the concept.
 */

const ROW_KEY_SOURCE_SEPARATOR = "__";
const SPLIT_LOINC_UNIT_KEY_PREFIX = "loinc-unit::";
const SPLIT_LOINC_UNIT_SEPARATOR = "::unit=";

/**
 * Drops the `__source` qualifier from a pin key (or a biomarker row's own code), leaving the
 * biomarker-row identity: a bare LOINC, or a `loinc-unit::<loinc>::unit=<unit>` wrapper kept
 * intact. This is the identity that separates a biomarker's genuinely-distinct unit-split
 * series while still collapsing its per-source rows (which share a base id and differ only by
 * source slug).
 */
export function stripPinKeySource(key: string): string {
  const separatorIndex = key.indexOf(ROW_KEY_SOURCE_SEPARATOR);
  return separatorIndex === -1 ? key : key.slice(0, separatorIndex);
}

/**
 * Reduces a stored pin key (or a biomarker row's own code) to its base LOINC by stripping
 * the `__source` suffix AND unwrapping any `loinc-unit::<loinc>::unit=<unit>` wrapper — i.e.
 * unit-split variants of one LOINC all collapse to the same base code.
 */
export function getLoincFromPinKey(key: string): string {
  const biomarkerId = stripPinKeySource(key);
  if (!biomarkerId.startsWith(SPLIT_LOINC_UNIT_KEY_PREFIX)) {
    return biomarkerId;
  }
  const remainder = biomarkerId.slice(SPLIT_LOINC_UNIT_KEY_PREFIX.length);
  const unitIndex = remainder.indexOf(SPLIT_LOINC_UNIT_SEPARATOR);
  return unitIndex === -1 ? biomarkerId : remainder.slice(0, unitIndex);
}

/**
 * True when `loincCode` (a bare LOINC, a source-qualified key, or a unit-split key) is pinned
 * by any stored key at BASE-LOINC granularity — unit-split variants of one LOINC all match.
 * Use for surfaces that render one row per LOINC concept (mobile overview).
 */
export function isLoincPinned(loincCode: string, pinnedRowKeys: readonly string[]): boolean {
  const target = getLoincFromPinKey(loincCode);
  return pinnedRowKeys.some((key) => getLoincFromPinKey(key) === target);
}

/**
 * True when `rowKey` is pinned at BIOMARKER-ROW granularity — the `__source` qualifier is
 * ignored on both sides (so a source-split biomarker matches a pin on any of its sources) but
 * a `loinc-unit::…::unit=…` variant only matches a pin on that same unit. Use for surfaces
 * that render one row per (LOINC, unit) — patient web, which expands rows by source/unit —
 * so distinct unit-split siblings aren't conflated into a single pin.
 */
export function isRowKeyPinned(rowKey: string, pinnedRowKeys: readonly string[]): boolean {
  const target = stripPinKeySource(rowKey);
  return pinnedRowKeys.some((key) => stripPinKeySource(key) === target);
}
